import React, { useRef, forwardRef, useImperativeHandle } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import styled from 'styled-components';

const RecaptchaWrapper = styled.div`
  margin: 20px 0;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 78px; /* Standard reCAPTCHA height */

  .grecaptcha-badge {
    visibility: visible !important;
  }
`;

const ErrorMessage = styled.div`
  color: #d32f2f;
  font-size: 14px;
  margin-top: 8px;
  text-align: center;
`;

/**
 * reCAPTCHA v2 component - Shows checkbox verification for suspicious traffic
 * Only displays when v3 score is below threshold (0.3)
 *
 * @param {Function} onChange - Callback when reCAPTCHA is verified (token) or expired (null)
 * @param {Function} onError - Callback when reCAPTCHA encounters an error
 * @param {String} theme - "light" or "dark" (default: "light")
 * @param {String} size - "normal" or "compact" (default: "normal")
 */
const RecaptchaV2 = forwardRef(({
  onChange,
  onError,
  theme = 'light',
  size = 'normal'
}, ref) => {
  const recaptchaRef = useRef();
  const siteKey = process.env.REACT_APP_RECAPTCHA_V2_SITE_KEY;

  // Expose reset method to parent components
  useImperativeHandle(ref, () => ({
    reset: () => {
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
      }
    },
    execute: () => {
      if (recaptchaRef.current) {
        recaptchaRef.current.execute();
      }
    }
  }));

  const handleChange = (token) => {
    if (onChange) {
      onChange(token);
    }

    // Push successful v2 verification to GTM dataLayer
    if (token) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'recaptcha_v2_verified',
        recaptchaV2Passed: true
      });
    }
  };

  const handleExpired = () => {
    if (onChange) {
      onChange(null);
    }
    console.warn('reCAPTCHA v2 expired, user needs to verify again');
  };

  const handleError = (error) => {
    console.error('reCAPTCHA v2 error:', error);
    if (onError) {
      onError(error);
    }
  };

  if (!siteKey) {
    console.error('reCAPTCHA v2 site key not found in environment variables');
    return (
      <RecaptchaWrapper>
        <ErrorMessage>
          reCAPTCHA configuration error. Please contact support.
        </ErrorMessage>
      </RecaptchaWrapper>
    );
  }

  return (
    <RecaptchaWrapper>
      <ReCAPTCHA
        ref={recaptchaRef}
        sitekey={siteKey}
        onChange={handleChange}
        onExpired={handleExpired}
        onErrored={handleError}
        theme={theme}
        size={size}
      />
    </RecaptchaWrapper>
  );
});

RecaptchaV2.displayName = 'RecaptchaV2';

export default RecaptchaV2;
